import { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Textarea,
  Button,
  Switch,
} from "@fluentui/react-components";
import { Send24Regular } from "@fluentui/react-icons";
import { createTicketsApi } from "../api/tickets";
import {
  createSignaturesApi,
  pickSignature,
  processSignaturePlaceholders,
  type EmailSignature,
} from "../api/signatures";
import type { Agent } from "../auth/TeamsAuthProvider";

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingHorizontalM,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

interface Props {
  ticketId: string;
  ticketNumber?: number;
  teamId?: string | null;
  agent: Agent;
  token: string;
  workspaceId: string | null;
  onSent: () => void;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function textToHtml(text: string): string {
  return escapeHtml(text).replace(/\r?\n/g, "<br>");
}

function styleSignature(html: string): string {
  // Apply compact styling so the email signature renders tight (matches web app)
  return html
    .replace(/<p(?![^>]*style)/g, '<p style="margin: 2px 0; line-height: 1.25;"')
    .replace(
      /<p([^>]*)style="([^"]*)"/g,
      '<p$1style="$2 margin: 2px 0; line-height: 1.25;"'
    )
    .replace(
      /<div(?![^>]*style)(?![^>]*class="email-content")/g,
      '<div style="margin: 2px 0; line-height: 1.25;"'
    )
    .replace(
      /<div([^>]*)style="([^"]*)"(?![^>]*class="email-content")/g,
      '<div$1style="$2 margin: 2px 0; line-height: 1.25;"'
    );
}

export default function ReplyComposer({
  ticketId,
  ticketNumber,
  teamId,
  agent,
  token,
  workspaceId,
  onSent,
}: Props) {
  const styles = useStyles();
  const [content, setContent] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !workspaceId) return;
    const api = createSignaturesApi(token, workspaceId);
    api
      .list()
      .then((res) => {
        if (!cancelled) setSignatures(res.signatures ?? []);
      })
      .catch(() => {
        if (!cancelled) setSignatures([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, workspaceId]);

  async function send() {
    const trimmed = content.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const api = createTicketsApi(token, workspaceId);

      if (isInternal) {
        // Internal notes go as plain text — no signature, no email.
        await api.addComment(ticketId, {
          content: trimmed,
          isInternal: true,
          contentType: "text",
        });
      } else {
        // Public reply: build HTML, append signature, add ticket ref.
        let html = textToHtml(trimmed);

        const signature = pickSignature(signatures, teamId);
        if (signature && signature.content) {
          const processed = processSignaturePlaceholders(signature.content, agent);
          const styled = styleSignature(processed);
          html += `<br><div style="line-height: 1.1; margin-top: 4px;">${styled}</div>`;
        }
        if (ticketNumber) {
          html += `<div style="margin-top: 16px; font-size: 10px; color: #605e5c;">Ticket #${ticketNumber}</div>`;
        }

        await api.addComment(ticketId, {
          content: html,
          isInternal: false,
          contentType: "html",
        });
      }

      setContent("");
      onSent();
    } finally {
      setSending(false);
    }
  }

  async function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      await send();
    }
  }

  return (
    <div className={styles.root}>
      <Textarea
        placeholder={isInternal ? "Add an internal note..." : "Reply to contact... (Ctrl+Enter to send)"}
        value={content}
        onChange={(_, d) => setContent(d.value)}
        onKeyDown={onKeyDown}
        rows={3}
        resize="vertical"
        style={isInternal ? { backgroundColor: tokens.colorPaletteYellowBackground2 } : undefined}
      />
      <div className={styles.toolbar}>
        <Switch
          label="Internal note"
          checked={isInternal}
          onChange={(_, d) => setIsInternal(d.checked)}
        />
        <Button
          appearance="primary"
          icon={<Send24Regular />}
          onClick={send}
          disabled={!content.trim() || sending}
        >
          {sending ? "Sending..." : isInternal ? "Add Note" : "Send Reply"}
        </Button>
      </div>
    </div>
  );
}
